import React, { useState, useEffect } from 'react';
import { X, Calendar, Info, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { buildAuthHeaders } from '@/utils/api';
import config from '@/config';

interface UserAgeSettingsProps {
  isVisible: boolean;
  onClose: () => void;
}

const AGE_GROUP_OPTIONS = [
  { value: 'under-18', label: 'Under 18', icon: '👶', disabled: true },
  { value: '18-24', label: '18-24', icon: '🧑' },
  { value: '25-34', label: '25-34', icon: '👨' },
  { value: '35-44', label: '35-44', icon: '👨‍🦰' },
  { value: '45+', label: '45+', icon: '👴' }
];

const COOLDOWN_DAYS = 365; // 1 year

export function UserAgeSettings({ isVisible, onClose }: UserAgeSettingsProps) {
  const { user, getIdToken, getCsrfToken } = useAuth();
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>(
    user?.maybeAgeInfoFE?.maybeDeclared || ''
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [cooldownInfo, setCooldownInfo] = useState<{
    daysRemaining: number;
    canUpdateAt: string;
  } | null>(null);
  const [isInCooldown, setIsInCooldown] = useState(false);

  // Calculate cooldown status on mount and when user changes
  useEffect(() => {
    if (!user?.maybeAgeInfoFE?.maybeLastUpdated) {
      setIsInCooldown(false);
      setCooldownInfo(null);
      return;
    }

    const lastUpdated = new Date(user.maybeAgeInfoFE.maybeLastUpdated);
    const canUpdateAt = new Date(lastUpdated);
    canUpdateAt.setDate(canUpdateAt.getDate() + COOLDOWN_DAYS);

    const now = new Date();
    const millisecondsRemaining = canUpdateAt.getTime() - now.getTime();
    const daysRemaining = Math.ceil(millisecondsRemaining / (1000 * 60 * 60 * 24));

    if (daysRemaining > 0) {
      setIsInCooldown(true);
      setCooldownInfo({
        daysRemaining,
        canUpdateAt: canUpdateAt.toISOString()
      });
    } else {
      setIsInCooldown(false);
      setCooldownInfo(null);
    }
  }, [user]);

  // Update selected age group when user data changes
  useEffect(() => {
    if (user?.maybeAgeInfoFE?.maybeDeclared) {
      setSelectedAgeGroup(user.maybeAgeInfoFE.maybeDeclared);
    }
  }, [user]);

  if (!isVisible) return null;

  const handleAgeGroupUpdate = async () => {
    if (!selectedAgeGroup) {
      setError('Please select an age group');
      return;
    }

    if (selectedAgeGroup === 'under-18') {
      setError('You must be at least 18 years old to use this service');
      return;
    }

    if (selectedAgeGroup === user?.maybeAgeInfoFE?.maybeDeclared) {
      setError('Please select a different age group');
      return;
    }

    if (isInCooldown) {
      setError('You cannot update your age group yet. Please wait until the cooldown period ends.');
      return;
    }

    setIsUpdating(true);
    setError(null);
    setSuccess(false);
    setCooldownInfo(null);

    try {
      const token = getIdToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const csrfToken = getCsrfToken();
      const response = await fetch(`${config.baseUrl}/${config.endpoints.updateAgeGroup}`, {
        method: 'PUT',
        credentials: 'include',
        headers: buildAuthHeaders(token, csrfToken),
        body: JSON.stringify({ ageGroup: selectedAgeGroup })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle cooldown error
        if (response.status === 429 && data.daysRemaining !== undefined) {
          setCooldownInfo({
            daysRemaining: data.daysRemaining,
            canUpdateAt: data.canUpdateAt
          });
          setError(data.message || `You can update your age group again in ${data.daysRemaining} days`);
        } else {
          setError(data.message || 'Failed to update age group');
        }
        return;
      }

      // Success
      setSuccess(true);
      setTimeout(() => {
        // Refresh the page to get updated user data
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error('Error updating age group:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const currentAgeGroup = user?.maybeAgeInfoFE?.maybeDeclared;
  const lastUpdated = user?.maybeAgeInfoFE?.maybeLastUpdated;

  return (
    <div className="absolute top-16 right-4 w-96 bg-[#1C2431] border border-slate-700 rounded-lg shadow-xl z-30">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-400" />
            <h3 className="text-white font-semibold">Age Group Settings</h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current Age Group Display */}
        {currentAgeGroup && (
          <div className="mb-4 p-3 bg-[#0E1525] rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-slate-400 text-sm">Current Age Group</span>
              <span className="text-white font-medium">{currentAgeGroup}</span>
            </div>
            {lastUpdated && (
              <div className="mt-2 text-xs text-slate-500">
                Last updated: {new Date(lastUpdated).toLocaleDateString()}
              </div>
            )}
          </div>
        )}

        {/* Age Group Selection */}
        <div className="mb-4">
          <label className="text-white text-sm font-medium mb-2 block">
            Select Age Group
          </label>
          <div className="grid grid-cols-2 gap-2">
            {AGE_GROUP_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  if (!option.disabled) {
                    setSelectedAgeGroup(option.value);
                    setError(null);
                    setSuccess(false);
                  }
                }}
                disabled={isUpdating || isInCooldown || option.disabled}
                className={`p-3 rounded-lg text-sm font-medium transition-colors border ${
                  selectedAgeGroup === option.value
                    ? 'bg-purple-600 border-purple-500 text-white'
                    : option.disabled
                    ? 'bg-[#0E1525] border-red-600 text-slate-500 cursor-not-allowed opacity-50'
                    : 'bg-[#0E1525] border-slate-600 text-slate-300 hover:bg-[#1C2431] hover:text-white'
                } ${(isUpdating || isInCooldown) && !option.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span className="text-lg">{option.icon}</span>
                  <span>{option.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Info Message */}
        {isInCooldown && cooldownInfo ? (
          <div className="mb-4 p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-amber-300 text-sm font-medium">
                  You have already changed your age group
                </p>
                <p className="text-slate-300 text-xs mt-1">
                  You need to wait {cooldownInfo.daysRemaining} {cooldownInfo.daysRemaining === 1 ? 'day' : 'days'} to change it again.
                </p>
                <p className="text-slate-400 text-xs mt-1">
                  Available on: {new Date(cooldownInfo.canUpdateAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-slate-300 text-xs">
                You can update your age group once per year. Choose carefully.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-300 text-sm">{error}</p>
                {cooldownInfo && (
                  <p className="text-red-400 text-xs mt-1">
                    Available: {new Date(cooldownInfo.canUpdateAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
              <p className="text-green-300 text-sm">Age group updated successfully!</p>
            </div>
          </div>
        )}

        {/* Update Button */}
        <Button
          onClick={handleAgeGroupUpdate}
          disabled={!selectedAgeGroup || selectedAgeGroup === 'under-18' || isUpdating || success || isInCooldown}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUpdating ? 'Updating...' : isInCooldown ? 'Update Locked' : 'Update Age Group'}
        </Button>
      </div>
    </div>
  );
}
